import React, { useEffect, useState } from "react";
import experienceDetailBG from "assets/images/others/host-side-images/experienceDetailBG.png";
import { ReactComponent as LocationIcon } from "assets/images/svgs/locationDark.svg";
import { ReactComponent as CalandarIcon } from "assets/images/svgs/calendarDark.svg";
import { ReactComponent as ClockIcon } from "assets/images/svgs/clockdark.svg";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import { ReactComponent as ScanIcon } from "assets/images/svgs/scan.svg";
import RsvpBreadcrumb, {
  RsvpBreadcrumbLink,
} from "components/breadcrumbs/RsvpBreadcrumb";
import ScannerCameraPopup from "components/popups/scanner";
import useUtils from "hooks/useUtils";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import useRsvp from "../useHooks";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { getFilePathWithBackendUrl } from "utils/helpers/common/http-methods";
import WorkshopSessionPopup from "components/popups/workshop-session";
import QrScanner from "components/qrcode-scanner";
import { AiOutlineClose } from "react-icons/ai";
import { EVENT_STATUS } from "utils/helpers/enums/shared.enum";
import ExperienceDetailHeader from "components/experience-detail-header";
import ImageCarousel from "components/image-carousel";

const RsvpExperiencesDetail = () => {
  const { getQueryParams } = useUtils();
  const [event, setEvent] = useState<EventDTO>(new EventDTO());
  const params = getQueryParams();

  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const { getEventById } = useRsvp();

  const getEventDataByID = async () => {
    if (params?.id) {
      await getEventById(params?.id, setEvent);
    }
  };

  useEffect(() => {
    getEventDataByID();
  }, []);

  // hide button on the base of startTime
  const lastSession = event.workshopSessions[event.workshopSessions.length - 1];
  const lastSessionDateTime = new Date(
    `${lastSession.startDateWithMonthAndYear} ${lastSession.endTime}`
  );
  const hasStarted = new Date() >= lastSessionDateTime;

  const visibleSessions = event.workshopSessions.slice(0, 3);
  const hasMoreSessions = event.workshopSessions.length > 3;

  const [isScannerModalOpen, setIsScannerModalOpen] = useState(false);
  const [isQrScannerOpen, setIsQrScannerOpen] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const closeModals = () => {
    setIsScannerModalOpen(false);
    setIsQrScannerOpen(false);
  };
  const breadcrumbLinks: RsvpBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.rsvpHome },
    { title: "Experiences Detail", path: siteRoutes.rsvpExperiencesDetail },
  ];

  const navigate = useNavigate();

  const goToViewReviews = (id: number) => {
    navigate(`${siteRoutes.rsvpViewRatings}?id=${id}`);
  };

  const goToAttendess = () => {
    navigate(siteRoutes.rsvpAttendess, {
      state: { eventData: event },
    });
  };

  const handleEventInProcess = (): boolean => {
    //if today's current time is between start and end time of the workshop session then return true
    return event.workshopSessions.some((session) => {
      const startTime = new Date(session.startDateTime);
      const endTime = new Date(session.endDateTime);
      const currentTime = new Date();
      return currentTime >= startTime && currentTime <= endTime;
    });
  };

  const handleScanAgain = () => {
    setScannedData(null);
    setTimeout(() => {
      setIsQrScannerOpen(true);
    }, 100);
  };

  const handleModalClose = () => {
    setScannedData(null);
    setIsScannerModalOpen(true);
  };

  return (
    <div>
      <ExperienceDetailHeader
        event={event}
        breadcrumb={<RsvpBreadcrumb links={breadcrumbLinks} />}
        // onHostClick={goToHostDetail}
        onSeeMoreSessions={() => setWorkshopSessionModal(true)}
        onAttendeesClick={goToAttendess}
        visibleSessions={visibleSessions}
        hasMoreSessions={hasMoreSessions}
        gradientClass="bg-custom-gradient-rsvp"
      />

      <div className="container my-5">
        <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-6 ">
          <div className="text-blackie text-3xl">Overview</div>
          <div className="flex flex-wrap gap-3">
            {event.eventStatus === EVENT_STATUS.POSTED &&
              handleEventInProcess() && (
                <button
                  onClick={() => setIsScannerModalOpen(true)}
                  className="px-5 py-3 flex items-center gap-2 rounded-full text-white bg-rsvpgray cursor-pointer"
                >
                  <ScanIcon />
                  Scan
                </button>
              )}
             {event.eventStatus === EVENT_STATUS.COMPLETED && (
              <button
                onClick={() => goToViewReviews(event?.id)}
                className="px-5 py-3 flex items-center justify-center gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
              >
                View Ratings
              </button>
            )}
          </div>
        </div>
        <div className="w-full flex flex-wrap md:flex-nowrap my-5 md:flex-row flex-col-reverse md:gap-6">
          <div className="w-full md:w-[60%] mt-5 md:mt-0">
            <div>
              <h3 className="text-blackie text-xl font-medium mb-2">
                About Experience
              </h3>
              <p className="text-lightBlackText text-base">
                {event?.description || "Description"}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Age Group
                </h3>
                <p className="text-lightBlackText text-base capitalize">
                  {event?.ageGroup
                    ? event.ageGroup.replace(/_/g, " ")
                    : "Age Group"}
                </p>
              </div>
              <div>
                <div className="mb-4">
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Language
                  </h3>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-fade text-blackie rounded-lg text-base">
                      {event?.language || "Language"}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Skill Level
                </h3>
                <p className="text-lightBlackText text-base capitalize">
                  {event?.skillLevel.replaceAll("_", " ") || "Skill Level"}
                </p>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-medium text-blackie mb-2">
                Details
              </h2>

              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Learning Activity
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.description || ""}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                <div>
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Core Concepts
                  </h3>
                  <p className="text-lightBlackText text-base uppercase">
                    {event.coreConcept.join(", ")}
                  </p>
                </div>
                <div>
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Parent Supervision
                  </h3>
                  <p className="text-lightBlackText text-base">
                    {event?.parentSupervision ? "Yes" : "No"}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-blackie mb-6">
                Mission
              </h2>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Mission
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.host?.missionStatement || "--"}
                </p>
              </div>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2 mt-3">
                  About{" "}
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.host?.about || ""}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                <div className="">
                  <p className="text-blackie  font-medium text-xl">
                    Business Name
                  </p>
                  <p className="text-lightBlackText text-base">
                    {event?.host?.businessName || ""}
                  </p>
                </div>
                {event?.host?.businessWebsite && (
                  <div className="">
                    <p className="text-blackie text-xl font-medium ">
                      Business Website
                    </p>
                    <a
                      href={event?.host?.businessWebsite || ""}
                      className="text-blue-600 text-base"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {event?.host?.businessWebsite || ""}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full md:w-[40%] ">
            <div className="flex flex-col">
              <h3 className="text-blackie text-xl font-medium mb-4">
                Event Images
              </h3>
              <ImageCarousel
                images={event?.eventPicsUrl || []}
                getImagePath={getFilePathWithBackendUrl}
                height="h-[250px] lg:h-[300px]"
                autoPlayInterval={6000}
                showIndicators={true}
              />
            </div>
          </div>
        </div>
      </div>
      {isScannerModalOpen && (
        <ScannerCameraPopup
          isOpen={isScannerModalOpen}
          onClose={closeModals}
          handleOpenCamera={() => setIsQrScannerOpen(true)}
        />
      )}

      {isQrScannerOpen && (
        <QrScanner
          isOpen={isQrScannerOpen}
          onClose={closeModals}
          closeMe={() => {
            setIsQrScannerOpen(false);
          }}
          setIsScannerModalOpen={setIsScannerModalOpen}
          scannedData={scannedData}
          setScannedData={setScannedData}
          handleScanAgain={handleScanAgain}
        />
      )}

      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={event.workshopSessions}
        />
      )}

      {/* Modal with Scanned Data */}
      {scannedData && (
        <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-lg w-[90%] max-w-md relative">
            <button
              type="button"
              className="absolute top-2 right-3 text-xl font-bold text-gray-600 hover:text-red-500"
              onClick={handleModalClose}
            >
              <AiOutlineClose size={20} />
            </button>
            <div className="text-center">
              <img
                src={`${process.env.REACT_APP_MEDIA_BASE_URL}${scannedData.userProfile}`}
                alt="Profile"
                className="w-20 h-20 mx-auto rounded-full mb-4"
              />
              <h2 className="text-lg font-semibold">{scannedData.userName}</h2>
              <p className="text-sm text-gray-600">@{scannedData.userHandle}</p>
              {/* <div className="mt-4 text-left text-sm">
                      <p><strong>Event ID:</strong> {scannedData.eventId}</p>
                      <p><strong>Attendee ID:</strong> {scannedData.attendeeId}</p>
                      <p><strong>Child ID:</strong> {scannedData.childId}</p>
                      <p><strong>Workshop ID:</strong> {scannedData.workShopId}</p>
                    </div> */}
              {/* Scan Again Button */}
              <button
                type="button"
                className="mt-5 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-all"
                onClick={handleScanAgain}
              >
                Scan Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RsvpExperiencesDetail;
